import { O as OutputEventType, I as InternalDownloadAsEvent } from './internal-download-as-event-b0b1e7d3.js';
import { I as InternalDropdownValueSelectedEvent } from './internal-dropdown-value-selected-event-310f527b.js';
import { I as InternalEventType } from './internal-event-types-8598fbfb.js';

const MessageType = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};
const MessageCode = {
  RESOURCE_LINK_COPIED_SUCCESSFULLY: 'resource_link_copied_successfully',
  QUERY_IS_RUNNING: 'query_is_running',
  EXPLAIN_NOT_ALLOWED: 'explain_not_allowed',
  EXPLAIN_EXIT_FULLSCREEN: 'explain_exit_fullscreen',
  EXPLAIN_PLAN_COPIED_SUCCESSFULLY: 'explain_plan_copied_successfully',
  YASR_EXIT_FULLSCREEN: 'yasr_exit_fullscreen',
};
class InternalNotificationMessageEvent {
  constructor(code, messageType, message) {
    this.TYPE = InternalEventType.INTERNAL_NOTIFICATION_MESSAGE_EVENT;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_NOTIFICATION_MESSAGE_EVENT;
    this.payload = { code, messageType, message };
  }
}

class InternalShowResourceCopyLinkDialogEvent {
  constructor(copyLink) {
    this.TYPE = InternalEventType.INTERNAL_SHOW_RESOURCE_COPY_LINK_DIALOG_EVENT;
    this.payload = { copyLink };
  }
}

class InternalQueryExecuted {
  constructor(duration, tabId) {
    this.TYPE = InternalEventType.INTERNAL_QUERY_EXECUTED;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_QUERY_EXECUTED;
    this.payload = { duration, tabId };
  }
}

class InternalQueryEvent {
  constructor(request, query, queryMode, queryType, pageSize) {
    this.TYPE = InternalEventType.INTERNAL_QUERY_EVENT;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_QUERY_EVENT;
    this.payload = { request, query, queryMode, queryType, pageSize };
  }
}

class InternalCountQueryEvent {
  constructor(request, query, queryMode, queryType, pageSize) {
    this.TYPE = InternalEventType.INTERNAL_COUNT_QUERY_EVENT;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_COUNT_QUERY_EVENT;
    this.payload = { request, query, queryMode, queryType, pageSize };
  }
}

class InternalCountQueryResponseEvent {
  constructor(response) {
    this.TYPE = InternalEventType.INTERNAL_COUNT_QUERY_RESPONSE_EVENT;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_COUNT_QUERY_RESPONSE_EVENT;
    this.payload = { response };
  }
}

class InternalRequestAbortedEvent {
  constructor(request, queryMode) {
    this.TYPE = InternalEventType.INTERNAL_REQUEST_ABORTED_EVENT;
    this.OUTPUT_TYPE = OutputEventType.OUTPUT_REQUEST_ABORTED;
    this.payload = { request, queryMode };
  }
}

class InternalKeyboardShortcutsClickedEvent {
  constructor() {
    this.TYPE = InternalEventType.INTERNAL_KEYBOARD_SHORTCUTS_CLICKED_EVENT;
  }
}

class InternalExplainQueryEvent {
  constructor() {
    this.TYPE = InternalEventType.INTERNAL_EXPLAIN_QUERY_EVENT;
  }
}

/**
 * The purpose of this service is to mitigate the issue where the stencil builtin Event decorator
 * doesn't work properly when injected in a service. The issue is similar to this
 * https://github.com/ionic-team/stencil/issues/2563 and even there are many mentions of the exactly
 * the same behavior we stumbled upon.
 *
 * This service is intended to be used in other services in order to help firing events which then
 * is expected to be caught by the stencil yasgui component.
 * The way we workaround the issue above to dispatch native DOM events instead of events through the
 * builtin EventEmitter in the stencil. This is kind of awkward approach, because we now have two
 * separate event systems in place.
 */
class EventService {
  /**
   * Emits an <code>internalEvent</code> wrapped in a native CustomEvent.
   *
   * @param internalEvent The event {@link InternalEvent}.
   */
  emit(internalEvent) {
    return EventService.emitInternalEvent(this._hostElement, internalEvent);
  }
  emitEvent(element, type, payload) {
    return EventService.emitFromInnerElement(element, type, payload);
  }
  static emitFromInnerElement(element, type, payload) {
    if (!element) {
      return;
    }
    const innerEvent = EventService.toInnerEvent(type, payload);
    if (innerEvent) {
      return EventService.emitInternalEvent(element.closest('.yasgui-host-element'), innerEvent);
    }
    const event = new CustomEvent(type, { detail: payload });
    element.dispatchEvent(event);
    return event;
  }
  static emitInternalEvent(element, internalEvent) {
    if (!element) {
      return;
    }
    const event = new CustomEvent(internalEvent.TYPE, { detail: internalEvent });
    element.dispatchEvent(event);
    return event;
  }
  static toInnerEvent(type, payload) {
    switch (type) {
      case InternalEventType.INTERNAL_DOWNLOAD_AS_EVENT:
        return new InternalDownloadAsEvent(payload.value, payload.pluginName, payload.query, payload.infer, payload.sameAs);
      case InternalEventType.INTERNAL_DROPDOWN_VALUE_SELECTED_EVENT:
        return new InternalDropdownValueSelectedEvent(payload.value);
      case InternalEventType.INTERNAL_NOTIFICATION_MESSAGE_EVENT:
        return new InternalNotificationMessageEvent(payload.code, payload.messageType, payload.message);
      case InternalEventType.INTERNAL_SHOW_RESOURCE_COPY_LINK_DIALOG_EVENT:
        return new InternalShowResourceCopyLinkDialogEvent(payload.copyLink);
      case InternalEventType.INTERNAL_QUERY_EXECUTED:
        return new InternalQueryExecuted(payload.duration, payload.tabId);
      case InternalEventType.INTERNAL_QUERY_EVENT:
        return new InternalQueryEvent(payload.request, payload.query, payload.queryMode, payload.queryType, payload.pageSize);
      case InternalEventType.INTERNAL_COUNT_QUERY_EVENT:
        return new InternalCountQueryEvent(payload.request, payload.query, payload.queryMode, payload.queryType, payload.pageSize);
      case InternalEventType.INTERNAL_COUNT_QUERY_RESPONSE_EVENT:
        return new InternalCountQueryResponseEvent(payload.response);
      case InternalEventType.INTERNAL_REQUEST_ABORTED_EVENT:
        return new InternalRequestAbortedEvent(payload.request, payload.queryMode);
      case InternalEventType.INTERNAL_KEYBOARD_SHORTCUTS_CLICKED_EVENT:
        return new InternalKeyboardShortcutsClickedEvent();
      case InternalEventType.INTERNAL_YASQE_DROPDOWN_ACTION_SELECTED_EVENT:
        return new InternalDropdownValueSelectedEvent(payload.value);
      case InternalEventType.INTERNAL_EXPLAIN_QUERY_EVENT:
        return new InternalExplainQueryEvent();
      default:
        throw Error('Can\'t find internal event definition for type: ' + type);
    }
  }
  get hostElement() {
    return this._hostElement;
  }
  set hostElement(value) {
    this._hostElement = value;
  }
}

export { EventService as E, InternalNotificationMessageEvent as I, MessageType as M, MessageCode as a };
